import { PayloadAction } from "@reduxjs/toolkit";
import {
  all, put, select, spawn, takeEvery
} from "redux-saga/effects";

import {
  removeIgnoredValues,
  retrieveIgnoredValues,
  retrieveStagingValues,
  saveMergedStagingValues
} from "API/Staging";
import { IMeta } from "Model/State/INotificationMeta";
import { IProject } from "Model/Storage/IProject";

import {
  projectsAdd,
  projectsExportDialog,
  projectsStagingManageDialog,
  projectsStagingManageError,
  projectsStagingManageRemoveError,
  projectsStagingManageRemoveStart,
  projectsStagingManageRemoveSuccess,
  projectsStagingManageStart,
  projectsStagingManageSuccess,
  projectsStagingMergeError,
  projectsStagingMergeStart,
  projectsStagingMergeSuccess,
  projectsStagingUpdateError,
  projectsStagingUpdateStart,
  projectsStagingUpdateSuccess
} from "Store/Actions";
import { selectSelectedOrActiveProjectIds } from "Store/Selectors/Projects";
import { isTrue } from "Utils/Boolean";
import { isNullOrWhiteSpace } from "Utils/String";
import { getProjectsById } from "Workers/Projects";
import { callWithAuth } from "./CallWithAuth";
import { sortAndFilterSaga } from "./Projects";

function* projectsStagingManageStartSaga() {
  const [projectId] = yield select(selectSelectedOrActiveProjectIds);
  if (isNullOrWhiteSpace(projectId)) {
    return;
  }

  try {
    const rejectedFields = yield callWithAuth(retrieveIgnoredValues, projectId);
    yield all([put(projectsStagingManageSuccess(rejectedFields)), put(projectsStagingManageDialog(true))]);
  } catch (e) {
    yield put(projectsStagingManageError({ text: `[Staging] ${e}` }));
  }
}

function* projectsStagingManageRemoveStartSaga(action: PayloadAction<string>) {
  const { payload: property } = action;
  const [projectId] = yield select(selectSelectedOrActiveProjectIds);

  try {
    yield callWithAuth(removeIgnoredValues, projectId, [property]);
    yield put(projectsStagingManageRemoveSuccess(property));
  } catch (e) {
    yield put(projectsStagingManageStart());
    yield put(projectsStagingManageRemoveError(e));
  }
}

function* projectsStagingUpdateStartSaga(action: PayloadAction<string>) {
  const { payload: projectId } = action;

  try {
    const stagingValues = yield callWithAuth(retrieveStagingValues, projectId);
    yield put(projectsStagingUpdateSuccess(stagingValues));
  } catch (e) {
    yield put(projectsStagingUpdateError(e));
  }
}

type ProjectsMergeStartAction = PayloadAction<IProject, "projects/staging/merge/start", IMeta>;
function* projectsStagingMergeStartSaga(action: ProjectsMergeStartAction) {
  const {
    payload,
    meta: { showNotification, withExport }
  } = action;
  try {
    const [projectId] = yield select(selectSelectedOrActiveProjectIds);
    yield callWithAuth(saveMergedStagingValues, projectId, payload);

    if (isTrue(withExport, false)) {
      yield put(projectsExportDialog(true));
    }

    const projects: IProject[] = yield callWithAuth(getProjectsById, [projectId]);
    yield all([put(projectsAdd(projects)), put(projectsStagingMergeSuccess(projectId, showNotification))]);
    yield spawn(sortAndFilterSaga);
  } catch (e) {
    console.error(e);
    yield put(projectsStagingMergeError());
  }
}

export function* StagingSaga(): unknown {
  yield all([
    takeEvery(projectsStagingManageStart, projectsStagingManageStartSaga),
    takeEvery(projectsStagingManageRemoveStart, projectsStagingManageRemoveStartSaga),
    takeEvery(projectsStagingUpdateStart, projectsStagingUpdateStartSaga),
    takeEvery(projectsStagingMergeStart, projectsStagingMergeStartSaga)
  ]);
}
